import './App.css';
import Home from 'components/Home/Home';
import Step1 from 'components/Step1/Step1';
import Step2 from 'components/Step2/Step2';
import Step3 from 'components/Step3/Step3';
import Step4 from 'components/Step4/Step4';
import Step5 from 'components/Step5/Step5';
import Step6 from 'components/Step6/Step6';
import Report from 'components/Report/Report';
import NotFound from 'components/common/NotFound/NotFound';
import RedirectNA from 'components/common/RedirectNA/RedirectNA';
import Library from 'components/Library/Library';
import Support from 'components/Support/Support';
import ExternalAccess from 'components/ExternalAccess/ExternalAccess';
import ExternalAccessLogin from 'components/ExternalAccessLogin/ExternalAccessLogin';
import ClientConfiguration from 'components/ClientConfiguration/ClientConfiguration';
import Loading from 'components/common/Loading/Loading';
import ExternalAccessLayout from 'components/common/ExternalClientLayout/ExternalAccessLayout';
import { Route, Switch, useHistory, Redirect, useLocation } from 'react-router-dom';
import { useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNaFetching } from 'hooks/useNaFetching';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { Security, SecureRoute, LoginCallback } from '@okta/okta-react';
import { oktaConfig } from 'config/oktaConfig';
import { getCountry } from 'utils/country';
import { checkIfExternalAccess, checkIfReadOnlyMode } from 'utils/externalAccess';
import { initFullStory } from 'utils/fullStory';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { initPendo } from 'utils/pendo';
import { useUserFetching } from 'hooks/useUserFetching';
import { useExternalAccessFetching } from 'hooks/useExternalAccessFetching';
import countries from 'constants/countries';
import paths from 'constants/paths';
import actions from 'store/actions';
import searchParams from 'constants/searchParams';
import { appConfig } from 'config/appConfig';

const oktaAuth = new OktaAuth(oktaConfig.oidc);

const initDatadogRum = () => {
  if (datadogRum.getInitConfiguration()) return;

  datadogRum.init({
    applicationId: appConfig.DATADOG_APPLICATION_ID,
    clientToken: appConfig.DATADOG_CLIENT_TOKEN,
    site: appConfig.DATADOG_SITE,
    service: appConfig.DATADOG_SERVICE,
    env: appConfig.DATADOG_ENV,
    version: '1.0.0',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    plugins: [reactPlugin({ router: false })],
  });
};

const ExternalAccessDenied = (props) => {
  const { t } = useTranslation();
  return (
    <ExternalAccessLayout showTitle={false} description={t(`externalAccess.${props.message}`)} />
  );
};

const App = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const na = useSelector((state) => state.na);
  const language = useSelector((state) => state.general.language);
  const externalAccessConfig = useSelector((state) => state.externalAccessConfig);
  const currentUser = useSelector((state) => state.user);

  const location = useLocation();
  const pathname = useMemo(() => location.pathname, [location]);
  const routeCountry = pathname.slice(1, 3).toUpperCase();
  const params = new URLSearchParams(location.search);
  const accessKey = params.get(searchParams.accessKey);
  const preview = params.get(searchParams.preview);

  const isExternalAccess = checkIfExternalAccess(pathname, params);
  const checkReadOnlyMode = useCallback(
    () => checkIfReadOnlyMode(na, externalAccessConfig, isExternalAccess),
    [externalAccessConfig, isExternalAccess, na],
  );
  const isReadOnlyMode = useMemo(checkReadOnlyMode, [checkReadOnlyMode]);

  const setAppMode = () => {
    dispatch(actions.general.setReadOnlyMode(isReadOnlyMode));
    dispatch(actions.general.setExternalAccessMode(isExternalAccess));
  };

  const setLanguage = (language) => {
    if (!language) return;
    dispatch(actions.general.setLanguage(language));
  };

  const [isLoadingUser, { fetchUser }] = useUserFetching();
  const [isLoadingNa, { fetchNa }] = useNaFetching();
  const [isLoadingEa, { fetchExternalAccess }] = useExternalAccessFetching();
  const setDatadogUser = useCallback(() => {
    const userId = currentUser?.id ?? currentUser?.uid ?? currentUser?.email;
    if (userId == null || userId === '') return;

    datadogRum.setUser({
      id: userId,
      name: currentUser.name,
      email: currentUser.email,
    });
  }, [currentUser]);

  const appInit = () => {
    fetchUser();
    fetchNa(pathname);
    fetchExternalAccess(pathname, accessKey, preview);
    initPendo(currentUser, pathname, isExternalAccess);
    initFullStory(currentUser, pathname, isExternalAccess);
    initDatadogRum();
  };

  appInit();

  useEffect(() => {
    setAppMode();
  }, [isReadOnlyMode]);

  useEffect(() => {
    setDatadogUser();
  }, [setDatadogUser]);

  useEffect(() => {
    if (!na || language !== getCountry()) dispatch(actions.na.resetNA());
    setLanguage(getCountry());
  }, [routeCountry]);

  const allCountries = Object.values(countries).join('|');
  const homePathValidator = `/:country(${allCountries})`;
  const pdfReportPathValidator = `${homePathValidator}/${paths.report}`;
  const libraryPathValidator = `${homePathValidator}/${paths.library}`;
  const supportPathValidator = `${homePathValidator}/${paths.support}`;
  const redirectPathValidator = `${homePathValidator}/${paths.redirect}/:id(\\d+)`;
  const stepPathValidator = (step) => `${homePathValidator}/${paths.step}${step}/:id(\\d+)`;

  const clientConfigurationPathValidator = `${homePathValidator}/${paths.clientConfiguration}/:id(\\d+)`;
  const externalAccessPathValidator = `${homePathValidator}/${paths.externalAccess}`;
  const externalAccessLoginPathValidator = `${homePathValidator}/${paths.externalAccessLogin}`;
  const externalStepPathValidator = (step) =>
    `${homePathValidator}/${paths.externalAccess}/${paths.step}${step}/:id(\\d+)`;
  const externalAccessDeniedValidator = `${homePathValidator}/${paths.externalAccessDenied}`;
  const externalAccessBadRequestValidator = `${homePathValidator}/${paths.externalAccessBadRequest}`;
  const externalAccessRedirectPathValidator = `${homePathValidator}/${paths.externalAccess}/${paths.redirect}/:id(\\d+)`;

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    history.replace(toRelativeUrl(originalUri || paths.home, window.location.origin));
  };

  return isLoadingUser || isLoadingNa || isLoadingEa ? (
    <Loading />
  ) : (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <Switch>
        <Route path={paths.loginCallback} component={LoginCallback} />
        <SecureRoute path={paths.home} exact>
          <Redirect to={`${paths.home}${appConfig.DEFAULT_COUNTRY}`} />
        </SecureRoute>
        <SecureRoute path={homePathValidator} exact component={Home} />
        <SecureRoute
          path={stepPathValidator(paths.steps.currentSituation)}
          exact
          component={Step1}
        />
        <SecureRoute path={stepPathValidator(paths.steps.yourPeople)} exact component={Step2} />
        <SecureRoute path={stepPathValidator(paths.steps.yourGrowth)} exact component={Step3} />
        <SecureRoute path={stepPathValidator(paths.steps.spaceYouNeed)} exact component={Step4} />
        <SecureRoute
          path={stepPathValidator(paths.steps.customizeBuildOut)}
          exact
          component={Step5}
        />
        <SecureRoute path={stepPathValidator(paths.steps.scorecard)} exact component={Step6} />
        <SecureRoute path={redirectPathValidator} exact component={RedirectNA} />
        <SecureRoute path={libraryPathValidator} exact component={Library} />
        <SecureRoute path={supportPathValidator} exact component={Support} />
        <SecureRoute
          path={clientConfigurationPathValidator}
          exact
          component={ClientConfiguration}
        />
        <Route path={pdfReportPathValidator} component={Report} />
        <Route path={externalAccessRedirectPathValidator} exact component={RedirectNA} />

        <Route
          path={externalStepPathValidator(paths.steps.currentSituation)}
          exact
          component={Step1}
        />
        <Route path={externalStepPathValidator(paths.steps.yourPeople)} exact component={Step2} />
        <Route path={externalStepPathValidator(paths.steps.yourGrowth)} exact component={Step3} />
        <Route path={externalStepPathValidator(paths.steps.spaceYouNeed)} exact component={Step4} />
        <Route
          path={externalStepPathValidator(paths.steps.customizeBuildOut)}
          exact
          component={Step5}
        />
        <Route path={externalStepPathValidator(paths.steps.scorecard)} exact component={Step6} />

        <Route path={externalAccessPathValidator} component={ExternalAccess} />
        <Route path={externalAccessLoginPathValidator} component={ExternalAccessLogin} />
        <Route exact path={externalAccessDeniedValidator}>
          <ExternalAccessDenied message={'accessDenied'} />
        </Route>
        <Route exact path={externalAccessBadRequestValidator}>
          <ExternalAccessDenied message={'badRequest'} />
        </Route>
        <SecureRoute path="*" component={NotFound} />
      </Switch>
    </Security>
  );
};

export default App;
